import AudioFeedbackPlayer from '@modules/feedback/components/AudioType/AudioFeedbackPlayer/AudioFeedbackPlayer';
import { FeedbackCardMetadata } from '@modules/feedback/components/FeedbackCard/FeedbackCardMetadata';
import { FeedbackCategories } from '@modules/feedback/components/FeedbackCard/FeedbackCategories';
import FeedbackConversationPreview from '@modules/feedback/components/FeedbackCard/FeedbackConversationPreview';
import { FeedbackSummaryPreviewCard } from '@modules/feedback/components/FeedbackSummary';
import { FeedbackListViewType } from '@modules/feedback/constants';
import {
  useIsFeedbackOnFeedbackV2Enabled,
  useUpdateSelectedLabels,
} from '@modules/feedback/hooks';
import { useIsSnippetOrFeedbackSaved } from '@modules/savedFeedbackList/hooks';
import { Box, Divider, Stack } from '@mui/material';
import { useMemo } from 'react';
import {
  Feedback,
  FeedbackContentType,
  FeedbackLabel,
  FeedbackLabelType,
} from 'v2/core/entities/feedback';
import { EntityLabel, ReasonLabel } from 'v2/core/entities/feedback/labels';
import { FeedbackMetaData } from 'v2/store/useViewFeedbackStore';

interface FeedbackCardContentProps {
  feedback: Feedback;
  selectedLabels: FeedbackLabel[];
  setSelectedLabels: React.Dispatch<React.SetStateAction<FeedbackLabel[]>>;
  selectedFeedbackMetadata?: FeedbackMetaData[];
  isFeedbackDetailsDrawerVisible?: boolean;
  setIsFeedbackDetailsDrawerVisible?: (isVisible: boolean) => void;
  onViewDetailsButtonClick: (selectedLabels: FeedbackLabel[]) => void;
  disableLabelSelection?: boolean;
  disableClickToExpand?: boolean;
  hideMetadata?: boolean;
  enableScrollView?: boolean;
  isSummaryView: boolean;
  feedbackListViewType: FeedbackListViewType;
}

const FeedbackCardContent = ({
  feedback,
  selectedLabels,
  setSelectedLabels,
  isFeedbackDetailsDrawerVisible,
  setIsFeedbackDetailsDrawerVisible,
  onViewDetailsButtonClick,
  selectedFeedbackMetadata,
  disableLabelSelection,
  disableClickToExpand,
  hideMetadata,
  enableScrollView,
  isSummaryView,
  feedbackListViewType,
}: FeedbackCardContentProps) => {
  const { isFeedbackOnFeedbackV2Enabled } = useIsFeedbackOnFeedbackV2Enabled();

  const isSavedFeedback = useIsSnippetOrFeedbackSaved({
    feedbackID: feedback.id,
  });

  const shouldShowSavedFeedbackCard =
    isSavedFeedback && feedbackListViewType === FeedbackListViewType.DEFAULT;

  const shouldRenderPredictionsAndMetadata =
    feedbackListViewType !== FeedbackListViewType.SAMPLE;

  const entityLabels = useMemo(
    (): EntityLabel[] =>
      feedback.labels?.filter(
        (label): label is EntityLabel =>
          label.type === FeedbackLabelType.Entity,
      ) ?? [],
    [feedback.labels],
  );

  const reasonLabels = useMemo(
    (): ReasonLabel[] =>
      feedback.labels?.filter(
        (label): label is ReasonLabel =>
          label.type === FeedbackLabelType.Reason,
      ) ?? [],
    [feedback.labels],
  );

  const sentimentLabel = useMemo(
    (): FeedbackLabel | undefined =>
      feedback.labels?.find(
        (label) => label.type === FeedbackLabelType.Sentiment,
      ),
    [feedback.labels],
  );

  const selectedMetadataIDs = useMemo(
    () => selectedFeedbackMetadata?.map((metadata) => metadata.id),
    [selectedFeedbackMetadata],
  );

  useUpdateSelectedLabels({
    feedbackID: feedback.id,
    selectedLabels,
    setSelectedLabels,
  });

  const handleOnSetIsFeedbackDetailsDrawerVisible = (isVisible: boolean) => {
    setIsFeedbackDetailsDrawerVisible?.(isVisible);
    onViewDetailsButtonClick(selectedLabels);
  };

  const contentNode = () => {
    if (feedback.content.type === FeedbackContentType.Audio) {
      return (
        <AudioFeedbackPlayer
          isFeedbackCardView
          selectedLabels={selectedLabels}
          setSelectedLabels={setSelectedLabels}
          shouldAutoPause={isFeedbackDetailsDrawerVisible}
          feedback={feedback}
          onExpandFeedbackDetails={() =>
            handleOnSetIsFeedbackDetailsDrawerVisible(true)
          }
          feedbackListViewType={feedbackListViewType}
          shouldShowSavedFeedbackCard={shouldShowSavedFeedbackCard}
        />
      );
    }

    if (
      isSummaryView &&
      (feedback.content.summary?.units?.length || isFeedbackOnFeedbackV2Enabled)
    ) {
      return (
        <FeedbackSummaryPreviewCard
          feedback={feedback}
          selectedLabels={selectedLabels}
          openFeedbackDetailsDrawer={() =>
            handleOnSetIsFeedbackDetailsDrawerVisible(true)
          }
          shouldShowSavedFeedbackCard={shouldShowSavedFeedbackCard}
          feedbackListViewType={feedbackListViewType}
        />
      );
    }

    return (
      <FeedbackConversationPreview
        feedback={feedback}
        setIsFeedbackDetailsDrawerVisible={
          handleOnSetIsFeedbackDetailsDrawerVisible
        }
        selectedLabels={selectedLabels}
        disableClickToExpand={disableClickToExpand}
        enableScrollView={enableScrollView}
        shouldShowSavedFeedbackCard={shouldShowSavedFeedbackCard}
      />
    );
  };

  const renderPredictions = () => {
    return (
      <Box p={2}>
        <FeedbackCategories
          feedback={feedback}
          feedbackLabels={{ entityLabels, reasonLabels }}
          selectedLabels={selectedLabels}
          disabledLabelIDs={selectedMetadataIDs}
          disableLabelSelection={disableLabelSelection}
          setSelectedLabels={setSelectedLabels}
          shouldSortBySelection
          selectedFeedbackMetadata={selectedFeedbackMetadata}
          isFeedbackDetailsDrawerVisible={isFeedbackDetailsDrawerVisible}
        />
      </Box>
    );
  };

  return (
    <Box
      sx={{
        position: 'relative',
        opacity: 1,
      }}
    >
      <Stack>
        <Stack px={2} pb={2} spacing={1}>
          {contentNode()}
        </Stack>
        {shouldRenderPredictionsAndMetadata && (
          <>
            {renderPredictions()}
            {!hideMetadata && (
              <>
                <Divider />
                <Box px={2} py={1}>
                  <FeedbackCardMetadata
                    feedback={feedback}
                    sentimentLabel={sentimentLabel}
                    feedbackListViewType={feedbackListViewType}
                  />
                </Box>
              </>
            )}
          </>
        )}
      </Stack>
    </Box>
  );
};

export default FeedbackCardContent;
